/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { COOKIE_SETTINGS_HREF } from '@/lib/link/link-picker';
import { useSentinelLink } from '@/lib/link/use-sentinel-link';
import { openPreferenceCenter } from './onetrust.utils';

/**
 * Hrefs that reopen the OneTrust preference centre instead of navigating (UCR-2991).
 * `COOKIE_SETTINGS_HREF` is what the link picker's `type: 'cookieSettings'` resolves to; the
 * `#openlink-privacycenter` alias keeps parity with the SFRA `openLinks.js` sentinel, so a link
 * copied over from those sites works unchanged.
 */
const COOKIE_SETTINGS_HREFS = [COOKIE_SETTINGS_HREF, '#openlink-privacycenter'];

/**
 * Global "Cookie Settings" deep-link interceptor (UCR-2991). Mounts no UI.
 *
 * This is the shopper's only route back to their consent choices once the banner is dismissed, so
 * it is legally load-bearing rather than a convenience. Merchandisers author the link as a normal
 * footer link with the `cookieSettings` type.
 *
 * SFRA gets the same result by adding OneTrust's `ot-sdk-show-settings` class and letting the SDK
 * bind the click itself, but that binding is installed once at SDK load — a link rendered by a
 * later client-side navigation would never be wired up. Calling the API explicitly avoids that
 * whole class of bug. See {@link openPreferenceCenter}.
 *
 * Mounted **ungated** in root.tsx, like `<ChatLinkInterceptor>`: an authored CTA must never fall
 * through to a dead hash and push junk onto the history stack.
 */
export default function CookieSettingsLinkInterceptor(): null {
    useSentinelLink(COOKIE_SETTINGS_HREFS, () => {
        openPreferenceCenter();
    });

    return null;
}
